import React, { memo, useContext } from 'react';
import { Blok } from '../../interfaces/Blok.interface';
import { withBlokWrapper } from '../BlokWrapper/BlokWrapper';
import { Theme } from '../../interfaces/enums/Theme.enum';
import Heading, { HeadingTypes } from '../Heading/Heading';
import { storyblokEditable } from 'gatsby-source-storyblok';
import Image from '../Image/Image';
import Container from '../Utils/Container/Container';
import Grid from '../Utils/Grid/Grid';
import Col from '../Utils/Col/Col';
import { ArticleContext } from '../../contexts/ArticleContext';
import { StoryblokAsset } from '../../interfaces/StoryblokAsset.interface';
import Subtitle from '../Subtitle/Subtitle';
import { StoryblokNode } from '../../interfaces/StoryblokNode.interface';
import { StoryblokArticleAuthor } from '../../interfaces/StoryblokArticleAuthor.interface';
import ArticleHeroAuthor from './ArticleHeroAuthor';
import SbImage from '../SbImage/SbImage';

interface ArticleHeroBlokProps extends Blok {
  heading: string;
  body?: string;
  image: StoryblokAsset;
}

interface ArticleHeroProps {
  blok: ArticleHeroBlokProps;
}

const ArticleHero: React.FC<ArticleHeroProps> = ({ blok }) => {
  const { heading, body = undefined, image } = blok;
  const {
    category_tags,
    authors,
    readTime,
    publishedDateOverride,
    updatedDateOverride,
    updatedDate,
    publishedAt,
  } = useContext(ArticleContext);

  return (
    <section {...storyblokEditable(blok)} className="pt-16 xl:py-20">
      <Container startingBreakpoint={'xl'}>
        <Grid gapY="gap-y-10 sm:gap-y-16 xl:gap-y-0">
          <Col span={{ md: 10, xl: 5, xxl: 6 }} start={{ md: 2, xl: 1 }}>
            <div className="flex flex-col items-center justify-center px-4 space-y-8 xl:items-start">
              <div className="space-y-6 text-center xl:text-left">
                {category_tags && <Subtitle text={category_tags.join(' - ')} />}
                {heading && (
                  <Heading text={heading} headingType={HeadingTypes.h1} />
                )}
                {readTime && (
                  <p className="text-text/body-tint text-sm">
                    {readTime} min read
                  </p>
                )}
                {body && <p className="text-text/body text-xl">{body}</p>}
              </div>
              {authors && (
                <ul className="space-y-4">
                  {authors.map(
                    (author: StoryblokNode<StoryblokArticleAuthor>, idx) => (
                      <div key={`${author._uid}-${idx}`}>
                        <ArticleHeroAuthor
                          author={author}
                          publishedDateOverride={publishedDateOverride}
                          publishedAt={publishedAt}
                          updatedDateOverride={updatedDateOverride}
                          updatedDate={updatedDate}
                        />
                      </div>
                    )
                  )}
                </ul>
              )}
            </div>
          </Col>
          <Col span={{ xl: 6, xxl: 5 }} start={{ xl: 7, xxl: 8 }}>
            <div className="h-full flex items-center justify-center">
              <div className="w-full aspect-video">
                {image?.filename && (
                  <SbImage
                    asset={image}
                    options={{
                      width: 868,
                      sizes: { base: 500, sm: 769, xl: 689, xxl: 868 },
                      loading: 'eager',
                    }}
                  />
                )}
              </div>
            </div>
          </Col>
        </Grid>
      </Container>
    </section>
  );
};

export default memo(withBlokWrapper(ArticleHero, Theme.DARK));
